export interface Translations {
  // Main module
  userExperiences: string
  whatDoUsersSay: string
  whatDoUsersSaySingular: string
  writeReview: string
  noReviewsAvailable: string
  beFirstToReview: string
  loadingReviews: string
  errorLoadingReviews: string
  tryAgain: string
  clickOnReviewToViewFullContent: string

  // Verification info
  areReviewsReliable: string
  verifiedReviews: string
  verifiedReviewsDescription: string
  verificationProcess: string
  telephoneVerification: string
  confirmationOfUse: string
  validationOfExperiences: string

  // Review details
  userExperience: string
  verified: string
  notVerified: string
  rating: string
  advantages: string
  disadvantages: string
  helpful: string
  usageStatusUnknown: string
  atCompany: string

  // Form labels
  name: string
  role: string
  company: string
  companySize: string
  sector: string
  title: string
  fullReview: string
  pros: string
  cons: string
  userType: string
  email: string
  phone: string
  companyOptional: string // New translation key for optional company field

  // Form placeholders
  yourFullName: string
  yourJobTitle: string
  companyName: string
  selectSize: string
  selectSector: string
  selectRating: string
  selectStatus: string
  shortSummary: string
  describeExperience: string
  strongPoints: string
  couldBeBetter: string
  yourEmail: string
  yourPhone: string

  // Form options
  excellent: string
  good: string
  average: string
  poor: string
  bad: string
  currentUser: string
  formerUser: string

  // Form actions
  cancel: string
  publishReview: string
  submitting: string
  close: string

  // Form validation
  fillRequiredFields: string
  networkError: string
  invalidEmailFormat: string
  acceptTermsRequired: string
  fullReviewMinLength: string
  characterCount: string

  // Form sections
  shareExperience: string

  // Success messages
  reviewSubmitted: string
  reviewSubmittedMessage: string

  // Required field indicator
  required: string

  // Terms and privacy
  acceptTermsAndPrivacy: string
  termsOfUse: string
  privacyPolicy: string
  and: string
  termsUrl: string
  privacyUrl: string

  // Sector options
  productionAndIndustry: string
  trade: string
  logisticsAndTransport: string
  constructionAndInstallation: string
  services: string
  healthcareAndWelfare: string
  educationAndResearch: string
  realEstateAndBuildingManagement: string
  agriculturalSector: string
  energyAndEnvironment: string
  governmentAndPublicSector: string
  nonProfitAndNgos: string
  mediaCulturalAndCreativeSector: string
  ictAndTelecom: string
  tourismHospitalityAndRecreation: string

  // Company size options
  oneToTenEmployees: string
  elevenToTwentyFiveEmployees: string
  twentyFiveToFiftyEmployees: string
  fiftyToHundredEmployees: string
  hundredToTwoFiftyEmployees: string
  twoFiftyToFiveHundredEmployees: string
  fiveHundredToOneThousandEmployees: string
  moreThanOneThousandEmployees: string

  // New: Anonymous review info
  anonymousReviewInfo: string

  // New: Contact info validation text
  contactInfoValidationText: string
}

export const translations: Record<string, Translations> = {
  nl: {
    // Main module
    userExperiences: "[{productName} reviews]",
    whatDoUsersSay: "{count} reviews",
    whatDoUsersSaySingular: "1 review",
    writeReview: "Review schrijven",
    noReviewsAvailable: "Nog geen reviews beschikbaar voor {productName}",
    beFirstToReview: "Wees de eerste om een review te schrijven",
    loadingReviews: "Reviews laden...",
    errorLoadingReviews: "Fout bij het laden van reviews:",
    tryAgain: "Probeer opnieuw",
    clickOnReviewToViewFullContent: "Klik op een review om de volledige inhoud te bekijken.",

    // Verification info
    areReviewsReliable: "Zijn de reviews betrouwbaar?",
    verifiedReviews: "Geverifieerde reviews",
    verifiedReviewsDescription:
      "Reviews met een groen vinkje zijn geverifieerd door ICT Portal. Dit betekent dat we telefonisch contact hebben gehad met de reviewer om de authenticiteit van de bijdrage te controleren.",
    verificationProcess: "Het verificatieproces:",
    telephoneVerification: "Telefonische verificatie van identiteit en functie",
    confirmationOfUse: "Bevestiging van daadwerkelijk gebruik van het systeem",
    validationOfExperiences: "Validatie van de beschreven ervaringen",

    // Review details
    userExperience: "Review",
    verified: "Geverifieerd",
    notVerified: "Niet geverifieerd",
    rating: "Beoordeling",
    advantages: "Voordelen",
    disadvantages: "Nadelen",
    helpful: "nuttig",
    usageStatusUnknown: "Gebruiksstatus onbekend",
    atCompany: "bij {company}",

    // Form labels
    name: "Naam (of nickname)",
    role: "Functie",
    company: "Bedrijf",
    companySize: "Bedrijfsgrootte",
    sector: "Sector",
    title: "Titel van je review",
    fullReview: "Je volledige ervaring",
    pros: "Voordelen",
    cons: "Nadelen",
    userType: "Gebruiksstatus",
    email: "E-mail",
    phone: "Telefoonnummer",
    companyOptional: "Bedrijf (optioneel)", // New translation

    // Form placeholders
    yourFullName: "Je volledige naam",
    yourJobTitle: "Je functietitel",
    companyName: "Bedrijfsnaam",
    selectSize: "Selecteer grootte",
    selectSector: "Selecteer sector",
    selectRating: "Selecteer beoordeling",
    selectStatus: "Selecteer status",
    shortSummary: "Korte samenvatting van je ervaring",
    describeExperience: "Beschrijf je ervaring met het systeem in detail...",
    strongPoints: "Wat zijn de sterke punten? (één per regel)",
    couldBeBetter: "Wat kan beter? (één per regel)",
    yourEmail: "Je e-mailadres",
    yourPhone: "Je telefoonnummer",

    // Form options
    excellent: "5 - Uitstekend",
    good: "4 - Goed",
    average: "3 - Gemiddeld",
    poor: "2 - Slecht",
    bad: "1 - Zeer slecht",
    currentUser: "Huidig gebruiker",
    formerUser: "Voormalig gebruiker",

    // Form actions
    cancel: "Annuleren",
    publishReview: "Review publiceren",
    submitting: "Verzenden...",
    close: "Sluiten",

    // Form validation
    fillRequiredFields: "Vul alle verplichte velden in:",
    networkError: "Netwerkfout opgetreden",
    invalidEmailFormat: "Voer een geldig e-mailadres in",
    acceptTermsRequired: "Je moet akkoord gaan met de gebruiksvoorwaarden en het privacybeleid",
    fullReviewMinLength: "Geef een gedetailleerdere review (minimaal {minLength} tekens).",
    characterCount: "Minimaal {min} tekens vereist. Huidig: {current}",

    // Form sections
    shareExperience: "Deel je ervaring met {productName}",

    // Success messages
    reviewSubmitted: "Review verzonden!",
    reviewSubmittedMessage:
      "Bedankt voor je review! We zullen deze zo snel mogelijk valideren en je via e-mail op de hoogte stellen van de status.",

    // Required field indicator
    required: "*",

    // Terms and privacy
    acceptTermsAndPrivacy: "Ik ga akkoord met de {termsLink} en het {privacyLink}.",
    termsOfUse: "gebruiksvoorwaarden",
    privacyPolicy: "privacybeleid",
    and: "en",
    termsUrl: "/algemene-voorwaarden",
    privacyUrl: "/privacyverklaring",

    // Sector options
    productionAndIndustry: "Productie en Industrie",
    trade: "Handel",
    logisticsAndTransport: "Logistiek en Transport",
    constructionAndInstallation: "Bouw en Installatie",
    services: "Dienstverlening",
    healthcareAndWelfare: "Zorg en Welzijn",
    educationAndResearch: "Onderwijs en Onderzoek",
    realEstateAndBuildingManagement: "Vastgoed en Gebouwbeheer",
    agriculturalSector: "Agrarische sector",
    energyAndEnvironment: "Energie en Milieu",
    governmentAndPublicSector: "Overheid en Publieke Sector",
    nonProfitAndNgos: "Non-Profit en NGO's",
    mediaCulturalAndCreativeSector: "Media, Culturele en Creatieve Sector",
    ictAndTelecom: "ICT en Telecom",
    tourismHospitalityAndRecreation: "Toerisme, Horeca en Recreatie",

    // Company size options
    oneToTenEmployees: "1-10 medewerkers",
    elevenToTwentyFiveEmployees: "11-25 medewerkers",
    twentyFiveToFiftyEmployees: "26-50 medewerkers",
    fiftyToHundredEmployees: "51-100 medewerkers",
    hundredToTwoFiftyEmployees: "101-250 medewerkers",
    twoFiftyToFiveHundredEmployees: "251-500 medewerkers",
    fiveHundredToOneThousandEmployees: "501-1.000 medewerkers",
    moreThanOneThousandEmployees: "1.001+ medewerkers",

    // New: Anonymous review info
    anonymousReviewInfo:
      "<strong>Anoniem reviewen:</strong> gebruik een nickname in plaats van je echte naam.",

    // New: Contact info validation text
    contactInfoValidationText:
      "Onderstaande gegevens zijn ter validatie van de review, en worden niet gepubliceerd.",
  },

  en: {
    // Main module
    userExperiences: "[{productName} reviews]",
    whatDoUsersSay: "{count} reviews",
    whatDoUsersSaySingular: "1 review",
    writeReview: "Write review",
    noReviewsAvailable: "No reviews available yet for {productName}",
    beFirstToReview: "Be the first to write a review",
    loadingReviews: "Loading reviews...",
    errorLoadingReviews: "Error loading reviews:",
    tryAgain: "Try again",
    clickOnReviewToViewFullContent: "Click on a review to view the full content.",

    // Verification info
    areReviewsReliable: "Are the reviews reliable?",
    verifiedReviews: "Verified reviews",
    verifiedReviewsDescription:
      "Reviews with a green checkmark are verified by ICT Portal. This means we have had telephone contact with the reviewer to check the authenticity of the contribution.",
    verificationProcess: "The verification process:",
    telephoneVerification: "Telephone verification of identity and role",
    confirmationOfUse: "Confirmation of actual use of the system",
    validationOfExperiences: "Validation of the described experiences",

    // Review details
    userExperience: "User Experience",
    verified: "Verified",
    notVerified: "Not verified",
    rating: "Rating",
    advantages: "Advantages",
    disadvantages: "Disadvantages",
    helpful: "helpful",
    usageStatusUnknown: "Usage status unknown",
    atCompany: "at {company}",

    // Form labels
    name: "Name (or nickname)",
    role: "Role",
    company: "Company",
    companySize: "Company size",
    sector: "Sector",
    title: "Title of your review",
    fullReview: "Your complete experience",
    pros: "Advantages",
    cons: "Disadvantages",
    userType: "Usage status",
    email: "Email",
    phone: "Phone number",
    companyOptional: "Company (optional)", // New translation

    // Form placeholders
    yourFullName: "Your full name",
    yourJobTitle: "Your job title",
    companyName: "Company name",
    selectSize: "Select size",
    selectSector: "Select sector",
    selectRating: "Select rating",
    selectStatus: "Select status",
    shortSummary: "Short summary of your experience",
    describeExperience: "Describe your experience with the system in detail...",
    strongPoints: "What are the strong points? (one per line)",
    couldBeBetter: "What could be better? (one per line)",
    yourEmail: "Your email address",
    yourPhone: "Your phone number",

    // Form options
    excellent: "5 - Excellent",
    good: "4 - Good",
    average: "3 - Average",
    poor: "2 - Poor",
    bad: "1 - Bad",
    currentUser: "Current user",
    formerUser: "Former user",

    // Form actions
    cancel: "Cancel",
    publishReview: "Publish review",
    submitting: "Submitting...",
    close: "Close",

    // Form validation
    fillRequiredFields: "Fill in all required fields:",
    networkError: "Network error occurred",
    invalidEmailFormat: "Please enter a valid email address",
    acceptTermsRequired: "You must accept the terms of use and privacy policy",
    fullReviewMinLength: "Please provide a more detailed review (minimum {minLength} characters).",
    characterCount: "Minimum {min} characters required. Current: {current}",

    // Form sections
    shareExperience: "Share your experience with {productName}",

    // Success messages
    reviewSubmitted: "Review submitted!",
    reviewSubmittedMessage:
      "Thank you for your review! We will validate it as soon as possible and notify you via email about the status.",

    // Required field indicator
    required: "*",

    // Terms and privacy
    acceptTermsAndPrivacy: "I agree to the {termsLink} and {privacyLink}.",
    termsOfUse: "terms of use",
    privacyPolicy: "privacy policy",
    and: "and",
    termsUrl: "/en/terms",
    privacyUrl: "/en/privacy",

    // Sector options
    productionAndIndustry: "Production and Industry",
    trade: "Trade",
    logisticsAndTransport: "Logistics and Transport",
    constructionAndInstallation: "Construction and Installation",
    services: "Services",
    healthcareAndWelfare: "Healthcare and Welfare",
    educationAndResearch: "Education and Research",
    realEstateAndBuildingManagement: "Real Estate and Building Management",
    agriculturalSector: "Agricultural sector",
    energyAndEnvironment: "Energy and Environment",
    governmentAndPublicSector: "Government and Public Sector",
    nonProfitAndNgos: "Non-Profit and NGOs",
    mediaCulturalAndCreativeSector: "Media, Cultural and Creative Sector",
    ictAndTelecom: "ICT and Telecom",
    tourismHospitalityAndRecreation: "Tourism, Hospitality and Recreation",

    // Company size options
    oneToTenEmployees: "1-10 employees",
    elevenToTwentyFiveEmployees: "11-25 employees",
    twentyFiveToFiftyEmployees: "26-50 employees",
    fiftyToHundredEmployees: "51-100 employees",
    hundredToTwoFiftyEmployees: "101-250 employees",
    twoFiftyToFiveHundredEmployees: "251-500 employees",
    fiveHundredToOneThousandEmployees: "501-1.000 employees",
    moreThanOneThousandEmployees: "1.001+ employees",
    

    // New: Anonymous review info
    anonymousReviewInfo:
      "<strong>Anonymous review:</strong> You can write an anonymous review. Use a nickname instead of your real name.",

    // New: Contact info validation text
    contactInfoValidationText: "These data are used exclusively for review validation and are not published.",
  },

  es: {
    // Main module
    userExperiences: "Experiencias con {productName}",
    whatDoUsersSay: "{count} reseñas",
    whatDoUsersSaySingular: "1 reseña",
    writeReview: "Escribir reseña",
    noReviewsAvailable: "Aún no hay reseñas disponibles para {productName}",
    beFirstToReview: "Sé el primero en escribir una reseña",
    loadingReviews: "Cargando reseñas...",
    errorLoadingReviews: "Error al cargar reseñas:",
    tryAgain: "Intentar de nuevo",
    clickOnReviewToViewFullContent: "Haz clic en una reseña para ver el contenido completo.",
    // Verification info
    areReviewsReliable: "¿Son confiables las reseñas?",
    verifiedReviews: "Reseñas verificadas",
    verifiedReviewsDescription:
      "Las reseñas con una marca verde están verificadas por TIC Portal. Esto significa que hemos tenido contacto telefónico con el revisor para verificar la autenticidad de la contribución.",
    verificationProcess: "El proceso de verificación:",
    telephoneVerification: "Verificación telefónica de identidad y función",
    confirmationOfUse: "Confirmación del uso real del sistema",
    validationOfExperiences: "Validación de las experiencias descritas",

    // Review details
    userExperience: "Experiencia de Usuario",
    verified: "Verificado",
    notVerified: "No verificado",
    rating: "Calificación",
    advantages: "Ventajas",
    disadvantages: "Desventajas",
    helpful: "útil",
    usageStatusUnknown: "Estado de uso desconocido",
    atCompany: "en {company}",

    // Form labels
    name: "Nombre (o pseudónimo)",
    role: "Cargo",
    company: "Empresa",
    companySize: "Tamaño de empresa",
    sector: "Sector",
    title: "Título de tu reseña",
    fullReview: "Tu experiencia completa",
    pros: "Ventajas",
    cons: "Desventajas",
    userType: "Estado de uso",
    email: "Correo electrónico",
    phone: "Número de teléfono",
    companyOptional: "Empresa (opcional)", // New translation

    // Form placeholders
    yourFullName: "Tu nombre completo",
    yourJobTitle: "Tu título de trabajo",
    companyName: "Nombre de la empresa",
    selectSize: "Seleccionar tamaño",
    selectSector: "Seleccionar sector",
    selectRating: "Seleccionar calificación",
    selectStatus: "Seleccionar estado",
    shortSummary: "Resumen breve de tu experiencia",
    describeExperience: "Describe tu experiencia con el sistema en detalle...",
    strongPoints: "¿Cuáles son los puntos fuertes? (uno por línea)",
    couldBeBetter: "¿Qué podría ser mejor? (uno por línea)",
    yourEmail: "Tu dirección de correo electrónico",
    yourPhone: "Tu número de teléfono",

    // Form options
    excellent: "5 - Excelente",
    good: "4 - Bueno",
    average: "3 - Promedio",
    poor: "2 - Malo",
    bad: "1 - Muy malo",
    currentUser: "Usuario actual",
    formerUser: "Usuario anterior",

    // Form actions
    cancel: "Cancelar",
    publishReview: "Publicar reseña",
    submitting: "Enviando...",
    close: "Cerrar",

    // Form validation
    fillRequiredFields: "Completa todos los campos requeridos:",
    networkError: "Ocurrió un error de red",
    invalidEmailFormat: "Por favor ingresa una dirección de correo válida",
    acceptTermsRequired: "Debes aceptar los términos de uso y la política de privacidad",
    fullReviewMinLength: "Por favor proporciona una reseña más detallada (mínimo {minLength} caracteres).",
    characterCount: "Mínimo {min} caracteres requeridos. Actual: {current}",

    // Form sections
    shareExperience: "Comparte tu experiencia con {productName}",

    // Success messages
    reviewSubmitted: "¡Reseña enviada!",
    reviewSubmittedMessage:
      "¡Gracias por tu reseña! La validaremos lo antes posible y te notificaremos por correo electrónico sobre el estado.",

    // Required field indicator
    required: "*",

    // Terms and privacy
    acceptTermsAndPrivacy: "Acepto las {termsLink} y la {privacyLink}.",
    termsOfUse: "condiciones generales",
    privacyPolicy: "política de privacidad",
    and: "y",
    termsUrl: "/condiciones-generales",
    privacyUrl: "/declaracion-privacidad",

    // Sector options
    productionAndIndustry: "Producción e Industria",
    trade: "Comercio",
    logisticsAndTransport: "Logística y Transporte",
    constructionAndInstallation: "Construcción e Instalación",
    services: "Servicios",
    healthcareAndWelfare: "Salud y Bienestar",
    educationAndResearch: "Educación e Investigación",
    realEstateAndBuildingManagement: "Bienes Raíces y Gestión de Edificios",
    agriculturalSector: "Agrícola",
    energyAndEnvironment: "Energía y Medio Ambiente",
    governmentAndPublicSector: "Gobierno y Sector Público",
    nonProfitAndNgos: "Sin Fines de Lucro y ONGs",
    mediaCulturalAndCreativeSector: "Medios, Cultural y Creativo",
    ictAndTelecom: "TIC y Telecomunicaciones",
    tourismHospitalityAndRecreation: "Turismo, Hostelería y Recreación",

    // Company size options
    oneToTenEmployees: "1-10 empleados",
    elevenToTwentyFiveEmployees: "11-25 empleados",
    twentyFiveToFiftyEmployees: "26-50 empleados",
    fiftyToHundredEmployees: "51-100 empleados",
    hundredToTwoFiftyEmployees: "101-250 empleados",
    twoFiftyToFiveHundredEmployees: "251-500 empleados",
    fiveHundredToOneThousandEmployees: "501-1.000 empleados",
    moreThanOneThousandEmployees: "1.001+ empleados",

    // New: Anonymous review info
    anonymousReviewInfo:
      "Puedes escribir una reseña <strong>anónima:</strong> Utiliza un apodo en lugar de tu nombre real.",

    // New: Contact info validation text
    contactInfoValidationText:
      "Estos datos se utilizan exclusivamente para la validación de la reseña y no se publican.",
  },
}
